import { useWebAppGetFragmentSocialUploadsUploadById } from '@mi-front/api-v2';
import { DateTime } from 'luxon';

const POLLING_INTERVAL = 3000;

/**
 * Minimal upload shape for polling helpers. Matches fragment/composition social upload item DTOs structurally.
 */
export type SocialUploadPollable = {
  status: string;
  data?: unknown;
  platformFeatures?: { publicUrl?: string | null };
};

function isPublicationWithinOneHour(upload: SocialUploadPollable): boolean {
  const publishedAtIso = (() => {
    if (!upload.data || typeof upload.data !== 'object') {
      return undefined;
    }

    const maybePublishedAt = (upload.data as { publishedAt?: unknown }).publishedAt;
    return typeof maybePublishedAt === 'string' ? maybePublishedAt : undefined;
  })();

  if (!publishedAtIso) {
    // If the backend doesn't provide the publication time yet, we can't enforce the 1-hour cap.
    // Keep the previous behavior (keep polling when it should otherwise be polled).
    return true;
  }

  const publishedAt = DateTime.fromISO(publishedAtIso);
  if (!publishedAt.isValid) {
    // Don't block polling if `publishedAt` is provided but can't be parsed.
    return true;
  }

  // diffNow is (publishedAt - now); we want (now - publishedAt) to ensure it's in the past.
  const ageHours = DateTime.now().diff(publishedAt).as('hours');
  return ageHours >= 0 && ageHours <= 1;
}

/** Returns true for statuses that require polling until terminal. */
export function isUploadPending(status: string | undefined): boolean {
  if (!status) {
    return false;
  }

  return (
    status === 'SCHEDULED' ||
    status === 'WAITING' ||
    status === 'WAITING_CHILDREN' ||
    status === 'IN_PROGRESS'
  );
}

/**
 * Returns true when the upload should be polled: either status is pending, or status is COMPLETED
 * but the publicUrl is still missing (e.g. backend has not set it yet).
 */
export function shouldPollUpload(upload: SocialUploadPollable | undefined): boolean {
  if (!upload) {
    return false;
  }

  // Hard stop: never poll for more than 1 hour after the publication time.
  // This prevents endless polling when the platform doesn't set `publicUrl` in time.
  if (!isPublicationWithinOneHour(upload)) {
    return false;
  }

  if (isUploadPending(upload.status)) {
    return true;
  }

  if (upload.status === 'COMPLETED') {
    return !upload.platformFeatures?.publicUrl;
  }

  return false;
}

/**
 * Polls a single social upload by id (BFF fragment social-uploads endpoint; response shape is shared with other contexts).
 * Refetches every 3s while status warrants polling. Caps refetch count at 30 updates.
 */
export function useSocialUploadPolling(
  uploadId: string | undefined,
  options: { enabled: boolean },
) {
  const { enabled } = options;

  return useWebAppGetFragmentSocialUploadsUploadById(uploadId ?? '', {
    query: {
      enabled: enabled && !!uploadId,
      refetchInterval: (q) => {
        const data = q.state.data as SocialUploadPollable | undefined;
        return shouldPollUpload(data) && q.state.dataUpdateCount < 30 ? POLLING_INTERVAL : false;
      },
    },
  });
}
